/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {login, loginVerify} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {getDashboardPath} from '../../../utils/helper'
import {KTIcon} from '../../../../_payairo/helpers'
import {generateAndSaveFCMToken} from '../../../../lib/firebase'

const loginSchema = Yup.object().shape({
  adminId: Yup.string().min(3, 'Minimum 3 symbols'),
  // .email('Email is required'),
})

const initialValues = {
  adminId: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        // Step 1: FastAPI POST /api/v1/users/admin/auth/login/ — admin JWT login
        const {data: authData} = await login(values.adminId, values.password)

        if (!authData || authData.status === false) {
          setLoading(false)
          setStatus('The login details are incorrect')
          setSubmitting(false)
          return
        }

        setLoading(false)
        console.log('Login Success:', authData.data)

        // Extract user data from response
        const userData = (authData.data?.user || authData.data) as any
        const userType = userData?.user_type || 'Admin'
        const role = userData?.role || authData.data?.role || userType
        const userId = userData?.id || authData.data?.userId || userData?.user_id
        const token = authData.data?.access_token || authData.data?.token
        const profileData = userData?.profileData || (authData.data as any)?.profileData

        // Store in localStorage as requested
      localStorage.setItem('access_token', token || '')
      localStorage.setItem('token', token || '')
      localStorage.setItem('refresh_token', authData.data?.refresh_token || '')
        localStorage.setItem('user_type', userType)
        localStorage.setItem('user', JSON.stringify(userData))

        // Store complete auth data with role-based fields
        const finalAuth = {
          ...authData.data,
          token: token,
          userId: userId,
          role: role,
          user_type: userType,
          profileData: profileData,
          user: {
            ...userData,
            role: role,
            user_type: userType,
            profileData: profileData,
          },
        }

        // Clear previous user state before setting new auth - prevents role flicker
        setCurrentUser(undefined)
        saveAuth(finalAuth as any)
        setCurrentUser({
          ...userData,
          role: role,
          user_type: userType,
          profileData: profileData,
        } as any)

        // ✅ FCM Token generation
        try {
          await generateAndSaveFCMToken()
        } catch (e) {
          console.warn('FCM registration failed', e)
        }

        navigate('/dashboard', {replace: true})
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const theme = localStorage.getItem('kt_theme_mode_value')

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        {theme == 'light' ? (
          <img src='/media/logos/payairo-color.png' className='w-100' />
        ) : (
          <img src='/media/logos/payairo-white.png' className='w-100' />
        )}
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Please enter the admin ID to login</div>
      </div>
      {/* begin::Heading */}

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        ''
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <div className='position-relative'>
          <input
            placeholder='Email'
            {...formik.getFieldProps('adminId')}
            className={clsx(
              'form-control bg-transparent pe-10',
              {'is-invalid': formik.touched.adminId && formik.errors.adminId},
              {
                'is-valid': formik.touched.adminId && !formik.errors.adminId,
              }
            )}
            type='text'
            name='adminId'
          />
        </div>
        {formik.touched.adminId && formik.errors.adminId && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.adminId}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
        <div className='position-relative'>
          <input
            placeholder='Password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent pe-15',
              {'is-invalid': formik.touched.password && formik.errors.password},
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            style={{backgroundImage: 'none'}}
            type={showPassword ? 'text' : 'password'}
            name='password'
            autoComplete='off'
          />
          <div className='position-absolute top-50 end-0 translate-middle-y d-flex align-items-center gap-2 me-3'>
            {formik.touched.password && !formik.errors.password && (
              <KTIcon iconName='check-circle' className='fs-2 text-success' />
            )}
            {formik.touched.password && formik.errors.password && (
              <KTIcon iconName='cross-circle' className='fs-2 text-danger' />
            )}
            <button
              type='button'
              className='btn btn-sm btn-icon'
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <KTIcon
                iconName={showPassword ? 'eye-slash' : 'eye'}
                className='fs-2 text-gray-500'
              />
            </button>
          </div>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.password}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Login</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <a href='https://payairo.com/contactus.html' className='link-primary' target='_blank'>
          Contact Us
        </a>
      </div>
    </form>
  )
}
